import $ from 'jquery';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { getRTL, testCSSFeature, horizontalResizeEvent } from './_utils';
import './scrollTrigger';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { action } from 'mobx';
import { onLoadComplete } from './_utils';
const classes = {
	navigation: 'Navigation',
	navigationItem: 'NavigationItem',
	navLogo: 'Navigation-logo',
	navLogoAnimate: 'Navigation-logo--animate',
	expanded: 'Navigation-more--expanded',
	bodySpacer: 'Navigation-bodySpacer',
	bodySpacerExpanded: 'Navigation-bodySpacer--expanded',
	overlay: 'NavigationOverlay',
	overlayOpen: 'NavigationOverlay--Opened',
	overlayContent: 'NavigationOverlay-InnerContent',
	stick: 'is-Stick',
	stickExpanded: 'is-StickExpanded',
	cta: 'Navigation-cta',
	ctaAnimate: 'Navigation-cta--animate',
	secondaryNav: 'SecondaryNavigation',
	secondaryOnlyContent: 'NavigationOverlay-InnerContent-OnlySecondary',
	secondaryNavPageNameActive: 'SecondaryNavigation-pageName--active',
	navigationcontent: 'NavigationOverlay-Content',
	overflowLinksWrapped: 'Navigation-overflowLinks--wrapped',
	navigationItemCollapsed: 'NavigationItem--collapsed',
	bannerShown: `Banner--shown`,
	bodyNavigationOverlayShown: `Body-navigationOverlay--shown`,
	moreBtnActive: `Navigation-moreBtn--active`,
	navHasHero: 'Navigation--hasHero',
	atTop: 'Navigation--atTop',
	interstitialOfferBanner: 'InterstitialOfferBanner',
};

const selectors = {
	parent: `.${classes.navigation}`,
	navBar: `.${classes.navigation}-bar`,
	navLg: `.${classes.navigation}-lg`,
	navLogoLg: `.${classes.navigation}-lg .${classes.navLogo}`,
	navTopBar: `.${classes.navigation}-topBar`,
	moreHover: `.${classes.navigation}-moreHover`,
	moreBtn: `.${classes.navigation}-moreBtn`,
	bodySpacer: `.${classes.bodySpacer}`,
	overlay: `.${classes.overlay}`,
	overlayContent: `.${classes.overlayContent}`,
	overlayCloseTrigger: '[data-navigation-close]',
	overlayOpenTrigger: '[data-navigation-open]',
	bgDisabledItems: 'a, button, select, input',
	bgExcludedItems: `:not(.${classes.overlay} *)`,
	navLarge: '[data-nav-large]',
	navMobile: '[data-nav-mobile]',
	secondaryNav: `.${classes.secondaryNav}`,
	secondaryNavCta: `.${classes.secondaryNav}-cta`,
	secondaryNavLogo: `.${classes.secondaryNav}-logoContainer`,
	secondaryNavCartStatus: `.${classes.secondaryNav}-cartStatus`,
	secondaryNavPageName: `.${classes.secondaryNav}-pageName`,
	secondarySmallContent: '.NavigationOverlay-LinksContainer .nav-sm',
	secondaryOnlyContent: `.${classes.secondaryOnlyContent}`,
	secondaryOnlyList: `.${classes.secondaryOnlyContent} ul`,
	navigationcontent: `.${classes.navigationcontent}`,
	mainLinksContainer: `.${classes.navigation}-mainLinksContainer`,
	mainLinks: `.${classes.navigation}-mainLinks`,
	navigationMore: `.${classes.navigation}-more--js`,
	overflowLinks: `.${classes.navigation}-overflowLinks`,
	overflowLinksLeft: `.${classes.navigation}-overflowLinks--left`,
	overflowLinksRight: `.${classes.navigation}-overflowLinks--right`,
	subLinksButton: `.${classes.navigationItem}-subLinksButton`,
	navigationOverlayLink: `.${classes.overlay}-Link`,
	navigationItem: `.${classes.navigationItem}`,
	navigationItemMainLink: `.${classes.navigationItem}--mainLink--js`,
	navigationItemMainLinkLast: `.${classes.navigationItem}--mainLink--js:last`,
	navigationItemSubLinks: `.${classes.navigationItem}-subLinks`,
	alertBanner: `.AlertBanner`,
	bannerCloseButton: `.Banner-closeButton`,
	background: '.Navigation-background',
	backgroundFilterFallback: '.Navigation-background-filter-fallback',
	acceptCookieButton: '#onetrust-accept-btn-handler',
	skipToMainContentButton: '.SkipToMainContent',
	stickyFooter: '.Navigation-sticky-footer',
	recaptchaContainer: '.recaptcha-container',
	interstitialOfferBanner: `.${classes.interstitialOfferBanner}`,
};

export const events = {
	smChange: 'navigation.sm.stateChange',
	lgChange: 'navigation.lg.stateChange',
	trigger: {
		collapse: 'navigation.large.collapse',
	},
};

if (!testCSSFeature('div', 'backdrop-filter')) {
	$(selectors.backgroundFilterFallback).css('display', 'block');
}

const $bgDisabledItems = $(selectors.bgDisabledItems).filter(selectors.bgExcludedItems);

export const navigationBreakpoint = 1100;
const mobileNavigationHeight = 60;
const ESCAPE_KEYCODE = 27;
const $window = $(window);

// Starts with null since the initial value will be set in the first call.
let isNavBarExpanded = null;

const $navMobile = $(selectors.navMobile);
const $navLarge = $(selectors.navLarge);

export const getNavigationBarHeight = () => {
	if ($(window).outerWidth() >= navigationBreakpoint) {
		return $navLarge.length > 0 ? parseInt($navLarge.css('height')) : 0;
	} else {
		return $navMobile.length > 0 ? parseInt($navMobile.css('height')) : 0;
	}
};

/**
 * Adds all the functionality required to open the overlay.
 * @param {jQuery} $parent parent element
 */
function setupOverlay($parent) {
	const $body = $('body');
	const $overlay = $parent.find(selectors.overlay);
	const $overlayContent = $overlay.find(selectors.overlayContent);
	const $openTrigger = $(selectors.overlayOpenTrigger);
	const $closeTrigger = $parent.find(selectors.overlayCloseTrigger);
	const $secondaryOnlyContent = $overlay.find(selectors.secondaryOnlyContent);

	// add the secondary links to the more navigation overlay
	if ($secondaryOnlyContent.length) {
		$(selectors.secondarySmallContent).clone().appendTo($(selectors.secondaryOnlyList));
	}

	const matchNavigationBreakpoint = () => {
		const navigationBreakpoint = 1100;
		const clientWidth = document.documentElement.clientWidth;
		return clientWidth >= navigationBreakpoint;
	};

	const onResize = () => {
		if (matchNavigationBreakpoint()) {
			closeOverlay();
		}
	};

	const openOverlay = () => {
		$body.addClass(classes.bodyNavigationOverlayShown);
		$overlay.addClass(classes.overlayOpen);
		$overlayContent.each((_index, element) => {
			disableBodyScroll(element);
		});

		$bgDisabledItems.attr('tabindex', -1);
	};

	const closeOverlay = () => {
		if ($overlay.hasClass(classes.overlayOpen)) {
			$overlay.removeClass(classes.overlayOpen);

			if ($body.hasClass(classes.bodyNavigationOverlayShown)) {
				const navigationOverlayTransitionTiming = 300; // milliseconds
				setTimeout(() => {
					$body.removeClass(classes.bodyNavigationOverlayShown);
				}, navigationOverlayTransitionTiming);
			}

			$overlayContent.each((_index, element) => {
				enableBodyScroll(element);
			});
			$bgDisabledItems.removeAttr('tabindex');
		}
	};

	$openTrigger.on('click', openOverlay);
	$closeTrigger.on('click', closeOverlay);

	$(window).on('resize', debounce(onResize, 200));

	$(document).on('keyup', e => {
		if (e.keyCode === ESCAPE_KEYCODE && $overlay.hasClass(classes.overlayOpen)) {
			closeOverlay();
		}
	});
}

/**
 * Animates the height of the nav bar passed as parameter. An
 * additional variable with the state is needed to prevent running the
 * same state twice (like trying to expand it if it's already expanded).
 * @param {jQuery} $parent Parent element.
 * @param {jQuery} $navBar nav bar element which height will change.
 * @param {boolean} shouldExpand Defines how the $navBar will change.
 */
function animateHeight($parent, $navBar, shouldExpand) {
	if (shouldExpand === isNavBarExpanded) {
		return;
	}

	const $bodySpacer = $(selectors.bodySpacer);
	const curHeight = $navBar.outerHeight();

	if (shouldExpand) {
		// Expand body spacer
		$bodySpacer.addClass(classes.bodySpacerExpanded);

		// Collapse secondary navigation
		collapseSecondaryNavigation($parent).then(() => {
			// Update navigation height
			const oldNavHeight = $parent.outerHeight();
			const newNavHeight = getStuckHeight($parent);
			$parent.outerHeight(newNavHeight);

			// Trigger navigation height change event
			$parent.trigger(events.lgChange, {
				height: newNavHeight,
				oldHeight: oldNavHeight,
			});
		});
	} else {
		// Get navBar auto height
		$navBar.css('height', 'auto');
		const autoHeight = $navBar.outerHeight();

		// Set navBar height
		$navBar.outerHeight(curHeight);
		$navBar.outerHeight(autoHeight);

		// Collapse body spacer
		$bodySpacer.removeClass(classes.bodySpacerExpanded);

		// Expand secondary navigation
		expandSecondaryNavigation($parent).then($parent => {
			// Update navigation height
			const oldNavHeight = $parent.outerHeight();
			const newNavHeight = getUnstuckHeight($parent);
			$parent.outerHeight(newNavHeight);

			// Trigger navigation height change event
			$parent.trigger(events.lgChange, {
				height: newNavHeight,
				oldHeight: oldNavHeight,
			});
		});
	}

	isNavBarExpanded = shouldExpand;
}

/**
 * Animates secondary navigation expansion.
 * @param {jQuery} $parent Parent element.
 * @returns {Promise}
 */

function expandSecondaryNavigation($parent) {
	return new Promise((resolve, reject) => {
		const $secondaryNav = $parent.find(selectors.secondaryNav);
		if ($secondaryNav.length > 0) {
			const $secondaryNavCTA = $secondaryNav.find(selectors.secondaryNavCta);
			const $secondaryNavLogo = $secondaryNav.find(selectors.secondaryNavLogo);
			const $secondaryNavPageName = $secondaryNav.find(selectors.secondaryNavPageName);
			const $secondaryNavCartStatus = $secondaryNav.find(selectors.secondaryNavCartStatus);

			// Get secondary nav page name height
			function getSecondaryNavPageNameHeight($secondaryNavPageName) {
				var pageNameHeight = $secondaryNavPageName.data('height');

				if (!pageNameHeight) {
					const isEmpty = $secondaryNavPageName.hasClass(
						'SecondaryNavigation-pageName--empty'
					);
					if (isEmpty) {
						$secondaryNavPageName.css('max-height', 'none');
					} else {
						// Allow element to grow to full height first (while hidden)
						$secondaryNavPageName.css('height', 'auto');
						$secondaryNavPageName.css('visibility', 'hidden');
					}

					// Get and save height
					pageNameHeight = $secondaryNavPageName.outerHeight();
					$secondaryNavPageName.data('height', pageNameHeight);

					// Reset element to original state
					$secondaryNavPageName.css('height', '0');
					$secondaryNavPageName.css('visibility', 'visible');
				}

				return pageNameHeight;
			}

			let pageNameHeight = getSecondaryNavPageNameHeight($secondaryNavPageName);

			// Animate secondary navigation
			$secondaryNavCTA.stop().animate({ opacity: 1 }, 300);
			$secondaryNavLogo.stop().animate({ opacity: 1 }, 300);
			$secondaryNavCartStatus.stop().animate({ opacity: 1 }, 300);
			$secondaryNavPageName
				.stop()
				.animate({ opacity: 1, height: pageNameHeight }, 300, () => {
					$secondaryNavPageName.css('pointer-events', 'auto');
					$secondaryNavPageName.addClass(classes.secondaryNavPageNameActive);

					// Resolve once animations are finished running
					resolve($parent);
				});
		} else {
			// Resolve if secondary navigation is not found
			resolve($parent);
		}
	});
}

/**
 * Animates secondary navigation collapse.
 * @param {jQuery} $parent Parent element.
 * @returns {Promise}
 */

function collapseSecondaryNavigation($parent) {
	return new Promise((resolve, reject) => {
		const $secondaryNav = $parent.find(selectors.secondaryNav);
		if ($secondaryNav.length > 0) {
			const $secondaryNavCTA = $secondaryNav.find(selectors.secondaryNavCta);
			const $secondaryNavLogo = $secondaryNav.find(selectors.secondaryNavLogo);
			const $secondaryNavPageName = $secondaryNav.find(selectors.secondaryNavPageName);
			const $secondaryNavCartStatus = $secondaryNav.find(selectors.secondaryNavCartStatus);

			// Animate secondary navigation
			$secondaryNavCTA.stop().animate({ opacity: 0 }, 300);
			$secondaryNavLogo.stop().animate({ opacity: 0 }, 300);
			$secondaryNavCartStatus.stop().animate({ opacity: 0 }, 300);
			$secondaryNavPageName.stop().animate({ opacity: 0 }, 300, () => {
				$secondaryNavPageName.stop().animate({ height: 0 }, 300, () => {
					$secondaryNavPageName.css('pointer-events', 'none');
					$secondaryNavPageName.removeClass(classes.secondaryNavPageNameActive);
					// Resolve once animations are finished running
					resolve($parent);
				});
			});
		} else {
			// Resolve if secondary navigation is not found
			resolve($parent);
		}
	});
}

/**
 * Gets height of navigation when it is stuck to top of viewport.
 * @param {jQuery} $parent Parent element.
 * @returns {Int} Navigation height
 */

function getStuckHeight($parent) {
	const $navigationLg = $parent.find(selectors.background);
	return $navigationLg.innerHeight();
}

/**
 * Gets height of navigation when it is not stuck to top of viewport.
 * @param {jQuery} $parent Parent element.
 * @returns {Int} Navigation height
 */

function getUnstuckHeight($parent) {
	var unstuckHeight;
	const $navBar = $parent.find(selectors.navBar);
	const $secondaryNav = $parent.find(selectors.secondaryNav);

	// Start with $navBar's height
	unstuckHeight = $navBar.outerHeight();

	// If $secondaryNav is present, use it's height instead
	if ($secondaryNav.length > 0) {
		unstuckHeight = $secondaryNav.outerHeight();
	}

	return unstuckHeight;
}

/**
 * Adds sticky functionality for the mobile navbar.
 * @param {jQuery} $parent parent element
 */
function setupStickyOnMobile($parent) {
	const $navMobile = $parent.find(selectors.navMobile);
	const matchMediaSM = window.matchMedia(
		`only screen and (max-width: ${navigationBreakpoint - 1}px)`
	);

	const handleScroll = () => {
		if (!matchMediaSM.matches) {
			return;
		}

		const shouldStick = Math.round($parent.get(0).getBoundingClientRect().top) < 0;

		if ($navMobile.hasClass(classes.stick) && !shouldStick) {
			$navMobile.removeClass(classes.stick);
			$parent.trigger(events.smChange, {
				height: $navMobile.outerHeight(),
				sticky: false,
			});
			ScrollTrigger.refresh(true);
		}

		if (!$navMobile.hasClass(classes.stick) && shouldStick) {
			$navMobile.addClass(classes.stick);
			$parent.trigger(events.smChange, {
				height: $navMobile.outerHeight(),
				sticky: true,
			});
			ScrollTrigger.refresh(true);
		}
		if (shouldStick) {
			$navMobile.removeClass(classes.atTop);
		} else {
			$navMobile.addClass(classes.atTop);
		}
	};

	$window.on('scroll', throttle(handleScroll, 40)).on('scroll', debounce(handleScroll, 60));
	$(document).on('alertBannerAdded', () => {
		window.requestAnimationFrame(() => {
			handleScroll();
		});
	});
	if (matchMediaSM.matches) {
		handleScroll();
	}
	$(document).on(
		horizontalResizeEvent,
		debounce(() => {
			if (matchMediaSM.matches) {
				$parent.outerHeight(`${mobileNavigationHeight}px`);

				$parent.trigger(events.smChange, {
					height: $navMobile.outerHeight(),
					sticky: $navMobile.hasClass(classes.stick),
				});
				const navTop = Math.round($parent.get(0).getBoundingClientRect().top);
				if (navTop <= 0) {
					$navMobile.removeClass(classes.atTop);
				} else {
					$navMobile.addClass(classes.atTop);
				}
			} else {
				if ($parent.outerHeight() === mobileNavigationHeight) {
					const rect = $navLarge[0].getBoundingClientRect();
					$parent.outerHeight(rect.height + rect.top + 'px');
				}
			}
		}, 100)
	);
}

/**
 * Takes care of all the logic of the sticky functionality and interaction with
 * other sticky components.
 * @param {jQuery} $parent parent element
 */
function setupStickyOnLarge($parent) {
	const $navBar = $parent.find(selectors.navBar);
	const $navTopBar = $parent.find(selectors.navTopBar);
	const $navLarge = $parent.find(selectors.navLarge);
	const matchMediaLG = window.matchMedia(
		`only screen and (min-width: ${navigationBreakpoint}px)`
	);
	let isExpanded = !$navLarge.hasClass(classes.stick);

	const $bodySpacer = $(selectors.bodySpacer);
	const $secondaryNav = $parent.find(selectors.secondaryNav);
	const $firstDivAfterNav = $parent.nextUntil('#main', 'div').first();
	const updateBodySpacer = () => {
		if ($secondaryNav.length > 0) {
			if (matchMediaLG.matches) {
				$bodySpacer.css('transition', 'none');
				$bodySpacer.css('height', getStuckHeight($parent));
				$bodySpacer.css(
					'background-color',
					$firstDivAfterNav.length > 0
						? $firstDivAfterNav.css('background-color')
						: 'inherit'
				);
			} else {
				$bodySpacer.css('transition', '');
				$bodySpacer.css('height', '');
				$bodySpacer.css('background-color', '');
			}
		}
	};
	updateBodySpacer();

	$parent.on(events.trigger.collapse, () => {
		if (matchMediaLG.matches) {
			changeNavHeight(false);
		}
	});

	const changeNavHeight = shouldExpand => {
		if (shouldExpand && !isExpanded) {
			isExpanded = true;
			animateHeight($parent, $navBar, isExpanded);
			$navLarge.addClass(classes.stickExpanded);
			ScrollTrigger.refresh(true);
		}

		if (!shouldExpand && isExpanded) {
			isExpanded = false;
			animateHeight($parent, $navBar, isExpanded);
			$navLarge.removeClass(classes.stickExpanded);
			ScrollTrigger.refresh(true);
		}
	};

	const toggleStick = shouldStick => {
		const navTop = Math.round($parent.get(0).getBoundingClientRect().top);
		shouldStick = shouldStick || navTop < 0;
		if ($navLarge.hasClass(classes.stick) && !shouldStick) {
			$navLarge.removeClass(classes.stick);
			$parent.removeClass(classes.stick);
			changeNavHeight(true);
			ScrollTrigger.refresh(true);
		}

		if (!$navLarge.hasClass(classes.stick) && shouldStick) {
			$navLarge.addClass(classes.stick);
			$parent.addClass(classes.stick);
			changeNavHeight(false);
			ScrollTrigger.refresh(true);
		}
	};
	let heightDiff = 0;
	let accumulateScrollDiff = 0;
	const toggleExpandedState = (() => {
		const expandNavDistance = 200;
		let lastScrollPosition = 0;

		isNavBarExpanded = isExpanded;
		$navLarge.toggleClass(classes.stickExpanded, isExpanded);

		return function () {
			if (!$navLarge.hasClass(classes.stick)) {
				return;
			}

			const currentScrollPosition = $window.scrollTop();
			const oldScrollPosition = lastScrollPosition;
			const scrollingDown = currentScrollPosition > lastScrollPosition;
			const scrollingUp = currentScrollPosition < lastScrollPosition;
			accumulateScrollDiff += currentScrollPosition - oldScrollPosition;
			lastScrollPosition = currentScrollPosition;

			if ((scrollingDown && !isExpanded) || (scrollingUp && isExpanded)) {
				accumulateScrollDiff = 0;
				return;
			}
			if (Math.abs(accumulateScrollDiff) > heightDiff) {
				accumulateScrollDiff = 0;
				if (scrollingDown) {
					changeNavHeight(false);
				} else if (scrollingUp) {
					changeNavHeight(true);
				}
			}
		};
	})();
	const handleScroll = () => {
		if (matchMediaLG.matches) {
			toggleStick();
			toggleExpandedState();
		}
	};
	handleScroll();
	$window.on('scroll', throttle(handleScroll, 50)).on('scroll', debounce(handleScroll, 60));

	$window.on(
		'resize',
		debounce(() => {
			if (matchMediaLG.matches) {
				handleScroll();
			}
			updateBodySpacer();
		}, 100)
	);
	$parent.on(events.lgChange, function (e, data) {
		heightDiff = Math.abs(data.height - data.oldHeight);
	});
	$window.on('scrollend', () => {
		accumulateScrollDiff = 0;
	});
}

/**
 * Handles responsive desktop navigation overflow
 * @param {jQuery} $parent parent element
 */
function setupOverflowLinks($parent) {
	const $mainLinksContainer = $(selectors.mainLinksContainer);
	const $mainLinks = $(selectors.mainLinks);
	const $overflowLinks = $(selectors.overflowLinks);
	const $moreLink = $(selectors.navigationMore);
	const overflowLinks = $overflowLinks.data('overflow-links') || [];
	const fudgeFactor = 30; // In pixels

	const updateOverflow = overflowLinks => {
		const $overflowLinksLeft = $(selectors.overflowLinksLeft);
		const $overflowLinksRight = $(selectors.overflowLinksRight);

		if (overflowLinks) {
			overflowLinks.forEach(($overflowLink, index) => {
				let $target;

				if (getRTL()) {
					$target = index % 2 === 0 ? $overflowLinksRight : $overflowLinksLeft;
				} else {
					$target = index % 2 === 0 ? $overflowLinksLeft : $overflowLinksRight;
				}

				$target.prepend($overflowLink);
			});
		}
	};

	if ($mainLinks && $overflowLinks) {
		const $mainLinksNavItems = $mainLinks.children(selectors.navigationItemMainLink);
		let $overflowLinksItems = $overflowLinks.find(selectors.navigationItemMainLink);

		let mainLinksContainerWidth = $mainLinksContainer.outerWidth();
		let mainLinksWidth = 0;
		const mainLinksPadding =
			parseInt($mainLinks.css('padding-left'), 10) +
			parseInt($mainLinks.css('padding-right'), 10);
		mainLinksWidth += mainLinksPadding;
		mainLinksWidth += fudgeFactor;
		mainLinksWidth += $moreLink.outerWidth();
		$mainLinksNavItems.each((index, element) => {
			mainLinksWidth += $(element).outerWidth();
		});

		// Check if links are wider than container
		if (mainLinksWidth > mainLinksContainerWidth) {
			// Too many links
			var linksRemain = true;
			while (mainLinksWidth > mainLinksContainerWidth && linksRemain) {
				const $lastMainLink = $mainLinks.children(selectors.navigationItemMainLinkLast);

				if ($lastMainLink.length > 0) {
					const lastMainLinkWidth = $lastMainLink.outerWidth();

					mainLinksWidth = mainLinksWidth - lastMainLinkWidth;

					overflowLinks.push($lastMainLink);
					updateOverflow(overflowLinks);
				} else {
					linksRemain = false;
				}
			}
		} else {
			// Too few links
			while (mainLinksWidth <= mainLinksContainerWidth && $overflowLinksItems.length > 0) {
				const firstOverflowLink = overflowLinks[overflowLinks.length - 1];

				if (firstOverflowLink) {
					//add the link first so we get the true width
					$moreLink.before(firstOverflowLink);
					const firstOverflowLinkWidth = firstOverflowLink.outerWidth();

					if (mainLinksContainerWidth - mainLinksWidth > firstOverflowLinkWidth) {
						overflowLinks.pop();
					}

					updateOverflow(overflowLinks);
					mainLinksWidth = mainLinksWidth + firstOverflowLinkWidth;
				} else {
					break;
				}
			}
		}

		$overflowLinks.data('overflow-links', overflowLinks);
		$overflowLinksItems = $overflowLinks.find(selectors.navigationItemMainLink);

		if ($overflowLinksItems.length < 1) {
			$moreLink.hide();
		} else {
			$moreLink.show();
		}

		// Underline literal "MORE..." link
		const activeLinksInOverflow = $overflowLinks.find('.NavigationItem-link--active');
		const hasActiveLinksInOverflow = activeLinksInOverflow.length;

		if (hasActiveLinksInOverflow) {
			$moreLink.addClass(classes.moreBtnActive);
		} else {
			$moreLink.removeClass(classes.moreBtnActive);
		}

		// Wrapping item logic
		if ($overflowLinksItems.length > 3) {
			$overflowLinks.addClass(classes.overflowLinksWrapped);
		} else {
			$overflowLinks.removeClass(classes.overflowLinksWrapped);
		}
	}
}

function setupMoreLink($parent) {
	const $moreLink = $(selectors.navigationMore);
	$moreLink.on('click', e => {
		const $navigation = $(selectors.parent);
		const isExpanded = e.target.getAttribute('aria-expanded') === 'true' ? false : true;

		if (isExpanded) {
			$navigation.addClass('Navigation--moreLinkExpanded');
		} else {
			$navigation.removeClass('Navigation--moreLinkExpanded');
		}

		e.target.setAttribute('aria-expanded', isExpanded);
		$navigation.toggleClass('Navigation--moreLinkClicked');
	});

	$moreLink.hover(
		e => {
			const $navigation = $(selectors.parent);
			$navigation.addClass('Navigation--moreLinkExpanded');
		},
		e => {
			const $navigation = $(selectors.parent);
			$navigation.removeClass('Navigation--moreLinkExpanded');
		}
	);
}

/**
 * Initializes the mobile navigation's sublink functionality.
 * @param {jQuery} $parent parent element
 */
function setupSubLinks($parent) {
	const $allSubLinksButtons = $parent.find(selectors.subLinksButton);

	$allSubLinksButtons.each((index, subLinksButton) => {
		const $subLinksButton = $(subLinksButton);
		$subLinksButton.on('click', e => {
			const $parent = $(e.target).closest(selectors.navigationItem);
			$parent.toggleClass(classes.navigationItemCollapsed);

			const isCollapsed = $parent.hasClass(classes.navigationItemCollapsed);
			const isExpanded = !isCollapsed;

			// Update sub-links' button aria- attributes
			const $subLinksButton = $(e.target);
			$subLinksButton.attr('aria-expanded', isExpanded);

			// Update sub-link's aria- attributes
			const $subLinks = $parent.find(selectors.navigationItemSubLinks);
			$subLinks.attr('aria-hidden', isCollapsed);
		});
	});

	const activeCategoryButton = $(
		'.NavigationOverlay .NavigationItem-link--activeCategory button'
	);
	if (activeCategoryButton.length > 0) {
		activeCategoryButton.click();
	}
}

function setupSkipToMainContent() {
	let hasAcceptCookieButtonBeenClicked = false;

	$('body').one('mousedown', selectors.acceptCookieButton, e => {
		hasAcceptCookieButtonBeenClicked = true;
	});

	$('body').one('keydown', selectors.acceptCookieButton, e => {
		hasAcceptCookieButtonBeenClicked = true;
	});

	const $skipToMainContentButton = $(selectors.skipToMainContentButton);
	$skipToMainContentButton.one('focus', function checkForAcceptCookieButtonClick(e) {
		if (hasAcceptCookieButtonBeenClicked === true) {
			e.currentTarget.blur();
			document.getElementById('focus-reset').focus();
		}
	});
}

/**
 * Handles moving the navigation vertically to make room for alert banner
 * @param {jQuery} $parent
 */
function fixAlertBanner($parent) {
	$parent.attr('style', '');
	const $interstitialOfferBanner = $(selectors.interstitialOfferBanner);
	const $alertBanner = $(selectors.alertBanner);
	const $alertBannerClose = $alertBanner.find(selectors.bannerCloseButton);
	const isParentAbsolute =
		$parent.css('position') === 'absolute' || $parent.css('position') === 'fixed';
	if (isParentAbsolute) {
		$parent.css({ top: 'calc(var(--alertheight, 1px) - 1px)', position: 'absolute' });
	}

	$alertBannerClose.on('click', function (e) {
		$parent.css('top', $interstitialOfferBanner.outerHeight() || 0);
	});
}

/**
 * Handles moving the recaptcha badge if it exists and sticky footer is enabled
 */
function fixRecaptchaBadge() {
	const $recaptchaContainer = $(selectors.recaptchaContainer);
	const $stickyFooter = $(selectors.stickyFooter);
	if ($stickyFooter.length > 0 && $recaptchaContainer.length > 0) {
		$recaptchaContainer.addClass('hasStickyFooter');
	}
}

/**
 * Initializes the navigation functionalities.
 * @param {jQuery} $parent parent element
 */
function initNavigation($parent) {
	// work around for firing check rates call on mobile
	const updateSearch = action(e => {
		e.preventDefault();
		window.dispatchEvent(new Event('cawComponentOpen'));
	});

	$parent.find('.nav-sm .Navigation-CAW-cta').on('click', updateSearch);

	if (/Edge/.test(navigator.userAgent) || /Trident/.test(navigator.userAgent)) {
		$parent.addClass('isEdgeOrIE');
	}

	setupOverlay($parent);
	fixAlertBanner($parent);
	setupStickyOnMobile($parent);
	setupStickyOnLarge($parent);
	setupSubLinks($parent);
	setupOverflowLinks($parent);
	setupSkipToMainContent();

	$(document).ready(() => {
		setupOverflowLinks($parent);
		setupMoreLink($parent);
	});

	onLoadComplete(() => setupOverflowLinks($parent));

	$(window).on('cawComponentLoaded', () => {
		setupOverflowLinks($parent);
	});

	// Fix alert banner if/when it is added to page
	$(document).on('alertBannerAdded', function (e) {
		fixAlertBanner($parent);
	});
	// Fix alert banner if/when noAlertBanner event is emitted
	$(document).on('noAlertBanner', function (e) {
		fixAlertBanner($parent);
	});

	$(window).resize(() => {
		fixAlertBanner($parent);
		setupOverflowLinks($parent);
	});

	$(window).on('recaptchaIsLoaded', function (e) {
		fixRecaptchaBadge($parent);
	});
}

export default function init() {
	$(selectors.parent).each((_, parent) => {
		const $parent = $(parent);
		initNavigation($parent);
	});
}
